import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Icon from '../icons'
import Button from '../../elements/button'
import Text from '../../elements/Typo/Text'
import { URLS } from '../../_config'
import { formatNumberWithCommas, trackEvent } from '../../_common/functions'
import InfoDialog from './InfoDialog'
import { EXPERIMENT_TYPES } from '../../_common/constant'

interface ExperimentStatusModalProps {
    isModalOpen: boolean
    onClose: () => void
    data: any
    experiment_literal?: string
    experiment_status?: string
    experimentDetails?: any
}

const ExperimentStatusModal: React.FC<ExperimentStatusModalProps> = ({ isModalOpen, onClose, data, experiment_literal, experiment_status, experimentDetails }) => {
    const { t } = useTranslation()
    const informationExptDesignRef = useRef<any>(null)
    const informationDataQualityRef = useRef<any>(null)
    const informationMeasurementMethodologyRef = useRef<any>(null)
    const informationMeasurementOutcomeRef = useRef<any>(null)

    const [showInfoExptDesignDialog, setShowInfoExptDesignDialog] = useState<boolean>(false)
    const [showInfoDataQualityDialog, setShowInfoDataQualityDialog] = useState<boolean>(false)
    const [showInfoMeasurementMethodologyDialog, setShowInfoMeasurementMethodologyDialog] = useState<boolean>(false)
    const [showInfoMeasurementOutcomeDialog, setShowInfoMeasurementOutcomeDialog] = useState<boolean>(false)

    const getSymbol = (val1: number, val2: number) => {
        if (val1 == null || val2 == null) return ''
        if (val1 > val2) return '>'
        if (val1 < val2) return '<'
        return '='
    }

    useEffect(() => {
        if (isModalOpen) {
            trackEvent('experiment_status_modal_opened', { experiment_literal, experiment_status })
        }
    }, [isModalOpen])

    const _handleCloseModal = () => {
        trackEvent('experiment_status_modal_closed', { experiment_literal })
        onClose()
        // _ref.current?.close()
    }

    const toggleExptDesignDialog = () => {
        trackEvent('experiment_status_design_info_toggled', { experiment_literal, expanded: !showInfoExptDesignDialog })
        setShowInfoExptDesignDialog(!showInfoExptDesignDialog)
    }
    const toggleDataQualityDialog = () => {
        trackEvent('experiment_status_data_quality_info_toggled', { experiment_literal, expanded: !showInfoDataQualityDialog })
        setShowInfoDataQualityDialog(!showInfoDataQualityDialog)
    }
    const toggleMeasurementMethodologyDialog = () => {
        trackEvent('experiment_status_measurement_methodology_info_toggled', { experiment_literal, expanded: !showInfoMeasurementMethodologyDialog })
        setShowInfoMeasurementMethodologyDialog(!showInfoMeasurementMethodologyDialog)
    }
    const toggleMeasurementOutcomeDialog = () => {
        trackEvent('experiment_status_measurement_outcome_info_toggled', { experiment_literal, expanded: !showInfoMeasurementOutcomeDialog })
        setShowInfoMeasurementOutcomeDialog(!showInfoMeasurementOutcomeDialog)
    }

    return (
        <dialog className={`modal ${isModalOpen ? 'modal-open' : ''}`} data-testid="modal">
            <div className="modal-box bg-[#FFFFFF] rounded-[0.833rem] w-md max-w-xl">
                <div className="flex justify-end mb-2">
                    <button
                        type="button"
                        data-testid="icon-button"
                        className="text-white bg-black rounded-full h-[2rem] w-[2.4rem] flex justify-center items-center flex-end"
                        onClick={() => {
                            _handleCloseModal()
                        }}
                    >
                        <Icon name="close" size={'1rem'} color="white" />
                    </button>
                </div>

                <div className="bg-[#325A6D1A] rounded-[0.625rem] px-[1.042rem] py-[0.625rem]">
                    <Text size="xl" className="font-bold text-center">
                        {data?.title}
                    </Text>
                </div>
                <div>
                    {/* Experiment Design */}
                    <div className="mt-2">
                        <div className="flex items-center mb-2 relative" ref={informationExptDesignRef}>
                            <div style={{ backgroundColor: data?.['Experiment Design']?.color ?? '#a52626' }} className=" rounded-full w-[0.625rem] h-[0.625rem]"></div>
                            &nbsp;&nbsp;
                            <Text size="xl" className="font-bold">
                                {t('Experiment Design')}
                            </Text>{' '}
                            &nbsp;
                            {/* <Tooltip message={data?.['Experiment Design']?.message} color="info" position={'top'}>
                                    <Icon name="outlineInfoCircle" size={'0.45rem'} />
                                </Tooltip> */}
                            <Icon className="cursor-pointer" name="outlineInfoCircle" size={'0.45'} onClick={toggleExptDesignDialog} data-testid="exp-icon"></Icon>
                        </div>
                        {showInfoExptDesignDialog && <InfoDialog buttonRef={informationExptDesignRef} message={data?.['Experiment Design']?.message} toggleDialog={toggleExptDesignDialog} />}

                        {data?.['Experiment Design']?.contents[0] ? (
                            <div className="flex items-center ml-5 mb-3 gap-3">
                                <div className="w-[1.5rem]">
                                    <div style={{ backgroundColor: data?.['Experiment Design']?.contents[0]?.bullet_color ?? '#222222' }} className=" rounded-full w-[0.425rem] h-[0.425rem] mx-1"></div>
                                </div>
                                <div className="flex-grow">
                                    <Text size="md" className="font-normal">
                                        {data?.['Experiment Design']?.contents[0]?.text} : <span className="font-semibold">{data?.['Experiment Design']?.contents[0]?.value}</span>
                                    </Text>
                                </div>
                            </div>
                        ) : null}
                        {data?.['Experiment Design']?.contents[1] && !data?.is_sdk ? (
                            <div className="flex items-center ml-5 mb-3 gap-3">
                                <div className="w-[1.5rem]">
                                    <div style={{ backgroundColor: data?.['Experiment Design']?.contents[1]?.bullet_color ?? '#222222' }} className=" rounded-full w-[0.425rem] h-[0.425rem] mx-1"></div>
                                </div>

                                <div className="flex-grow grid grid-cols-12 items-center">
                                    <div className="col-span-5 bg-[#325A6D1A] rounded-[0.625rem] flex flex-col items-center px-[1.042rem] py-[0.625rem]">
                                        <Text size="md" className="font-normal">
                                            {t('Recommended Sample Size')}
                                        </Text>
                                        <Text size="lg" className="font-semibold">
                                            {formatNumberWithCommas(data?.['Experiment Design']?.contents[1]?.recommended_sample_size?.toFixed(2)) || 0}
                                        </Text>
                                    </div>
                                    <Text size="xxxl" className="col-span-2 text-center">
                                        {' '}
                                        {getSymbol(data?.['Experiment Design']?.contents[1]?.recommended_sample_size, data?.['Experiment Design']?.contents[1]?.actual_sample_size)}{' '}
                                    </Text>
                                    <div className="col-span-5 bg-[#325A6D1A] rounded-[0.625rem] flex flex-col items-center px-[1.042rem] py-[0.625rem]">
                                        <Text size="md" className="font-normal">
                                            {t('Actual Sample Size')}
                                        </Text>
                                        <Text size="lg" className="font-semibold !text-[#28A526]">
                                            {formatNumberWithCommas(data?.['Experiment Design']?.contents[1]?.actual_sample_size?.toFixed(2)) || 0}
                                        </Text>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>

                    {/* Data Quality */}
                    <div className="mt-2">
                        <div className="flex items-center mb-2 relative" ref={informationDataQualityRef}>
                            <div style={{ backgroundColor: data?.['Data Quality']?.color ?? '#a52626' }} className=" rounded-full w-[0.625rem] h-[0.625rem]"></div>
                            &nbsp;&nbsp;
                            <Text size="xl" className="font-bold">
                                {t('Data Quality')}
                            </Text>
                            &nbsp;
                            <Icon className="cursor-pointer" name="outlineInfoCircle" size={'0.45'} onClick={toggleDataQualityDialog}></Icon>
                        </div>
                        {showInfoDataQualityDialog && <InfoDialog buttonRef={informationDataQualityRef} message={data?.['Data Quality']?.message} toggleDialog={toggleDataQualityDialog} />}

                        {data?.['Data Quality']?.contents[0] ? (
                            <div className="flex items-center ml-5 mb-3 gap-3">
                                <div className="w-[1.5rem]">
                                    <div style={{ backgroundColor: data?.['Data Quality']?.contents[0]?.bullet_color ?? '#222222' }} className=" rounded-full w-[0.425rem] h-[0.425rem] mx-1"></div>
                                </div>

                                <div className="flex-grow">
                                    <Text size="md" className="font-normal">
                                        {data?.['Data Quality']?.contents[0]?.text} : <span className="font-semibold">{data?.['Data Quality']?.contents[0]?.value}</span>
                                    </Text>
                                </div>
                            </div>
                        ) : null}
                    </div>

                    {/* Measurement Metrics */}
                    <div className="mt-2">
                        <div className="flex items-center mb-2 relative" ref={informationMeasurementMethodologyRef}>
                            <div style={{ backgroundColor: data?.['Measurement Metrics']?.color ?? '#a52626' }} className=" rounded-full w-[0.625rem] h-[0.625rem]"></div>
                            &nbsp;&nbsp;
                            <Text size="xl" className="font-bold">
                                {t('Measurement Methodology')}
                            </Text>
                            &nbsp;
                            <Icon className="cursor-pointer" name="outlineInfoCircle" size={'0.45'} onClick={toggleMeasurementMethodologyDialog}></Icon>
                        </div>
                        {showInfoMeasurementMethodologyDialog && <InfoDialog buttonRef={informationMeasurementMethodologyRef} message={data?.['Measurement Metrics']?.message} toggleDialog={toggleMeasurementMethodologyDialog} />}

                        {data?.['Measurement Metrics']?.contents[0] ? (
                            <div className="flex items-center ml-5 mb-3 gap-3">
                                <div className="w-[1.5rem]">
                                    <div style={{ backgroundColor: data?.['Measurement Metrics']?.contents[0]?.bullet_color ?? '#222222' }} className=" rounded-full w-[0.425rem] h-[0.425rem] mx-1"></div>
                                </div>

                                <div className="flex flex-grow gap-3">
                                    <Text size="md" className="font-normal">
                                        {data?.['Measurement Metrics']?.contents[0]?.text} : <span className="font-semibold">{data?.['Measurement Metrics']?.contents[0]?.value}</span>
                                    </Text>

                                    {data?.['Measurement Metrics']?.contents[0]?.methodology_link && (
                                        <Text
                                            size="xs"
                                            className="underline cursor-pointer font-normal"
                                            onClick={() => {
                                                trackEvent('doc_experiment_status_know_more_clicked', { experiment_literal, experiment_status })
                                                window.open(data?.['Measurement Metrics']?.contents[0]?.methodology_link, '_blank', 'noopener,noreferrer')
                                            }}
                                        >
                                            {t('Click here to know more.')}
                                        </Text>
                                    )}
                                </div>
                            </div>
                        ) : null}
                    </div>

                    {/* Measurement Outcome */}
                    {experimentDetails?.experiment_type?.title != EXPERIMENT_TYPES.MTE.title && (
                        <div className="mt-2">
                            <div className="flex items-center mb-2 relative" ref={informationMeasurementOutcomeRef}>
                                <div style={{ backgroundColor: data?.['Measurement Outcome']?.color ?? '#a52626' }} className="rounded-full w-[0.625rem] h-[0.625rem]"></div>
                                &nbsp;&nbsp;
                                <Text size="xl" className="font-bold">
                                    {t('Measurement Outcome')}
                                </Text>
                                &nbsp;
                                <Icon className="cursor-pointer" name="outlineInfoCircle" size={'0.45'} onClick={toggleMeasurementOutcomeDialog}></Icon>
                            </div>
                            {showInfoMeasurementOutcomeDialog && <InfoDialog buttonRef={informationMeasurementOutcomeRef} message={data?.['Measurement Outcome']?.message} toggleDialog={toggleMeasurementOutcomeDialog} />}

                            {data?.['Measurement Outcome']?.contents[3] ? (
                                <div className="flex items-center ml-5 mb-3 gap-3">
                                    <div className="w-[1.5rem]">
                                        <div style={{ backgroundColor: data?.['Measurement Outcome']?.contents[3]?.bullet_color ?? '#222222' }} className=" rounded-full w-[0.425rem] h-[0.425rem] mx-1"></div>
                                    </div>
                                    <div className="flex-grow">
                                        <Text size="md" className="font-normal">
                                            {data?.['Measurement Outcome']?.contents[3]?.text} : <span className="font-semibold">{data?.['Measurement Outcome']?.contents[3]?.value?.toFixed(2)}</span>
                                        </Text>
                                    </div>
                                </div>
                            ) : null}

                            {data?.['Measurement Outcome']?.contents[0] ? (
                                <div className="flex items-center ml-5 mb-3 gap-3">
                                    <div className="w-[1.5rem]">
                                        <div style={{ backgroundColor: data?.['Measurement Outcome']?.contents[0]?.bullet_color ?? '#222222' }} className=" rounded-full w-[0.425rem] h-[0.425rem] mx-1"></div>
                                    </div>
                                    <div className="flex-grow">
                                        <Text size="md" className="font-normal">
                                            {data?.['Measurement Outcome']?.contents[0]?.text} : <span className="font-semibold">{data?.['Measurement Outcome']?.contents[0]?.value}</span>
                                        </Text>
                                    </div>
                                </div>
                            ) : null}

                            {data?.['Measurement Outcome']?.contents[1] ? (
                                <div className="flex items-center ml-5 mb-3 gap-3">
                                    <div className="w-[1.5rem]">
                                        <div style={{ backgroundColor: data?.['Measurement Outcome']?.contents[1]?.bullet_color ?? '#222222' }} className=" rounded-full w-[0.425rem] h-[0.425rem] mx-1"></div>
                                    </div>
                                    <div className="flex-grow grid grid-cols-12 items-center">
                                        <div className="col-span-5 bg-[#325A6D1A] rounded-[0.625rem] flex flex-col items-center px-[1.042rem] py-[0.625rem]">
                                            <Text size="md" className="font-normal">
                                                {t('Confidence Level')}
                                            </Text>
                                            <Text size="lg" className="font-semibold">
                                                {formatNumberWithCommas(data?.['Measurement Outcome']?.contents[1]?.actual_statistical_significance?.toFixed(2)) || 0} %
                                            </Text>
                                        </div>
                                        <Text size="xxxl" className="text-center col-span-2">
                                            {' '}
                                            {getSymbol(data?.['Measurement Outcome']?.contents[1]?.actual_statistical_significance, data?.['Measurement Outcome']?.contents[1]?.desired_statistical_significance)}{' '}
                                        </Text>
                                        <div className="col-span-5 bg-[#325A6D1A] rounded-[0.625rem] flex flex-col items-center px-[1.042rem] py-[0.625rem]">
                                            <Text size="md" className="font-normal">
                                                {t('Desired Confidence Level')}
                                            </Text>
                                            <Text size="lg" className="font-semibold !text-[#28A526]">
                                                {formatNumberWithCommas(data?.['Measurement Outcome']?.contents[1]?.desired_statistical_significance?.toFixed(2))} %
                                            </Text>
                                        </div>
                                    </div>
                                </div>
                            ) : null}

                            {data?.['Measurement Outcome']?.contents[2] ? (
                                <div className="flex items-center ml-5 mb-3 gap-3">
                                    <div className="w-[1.5rem]">
                                        <div style={{ backgroundColor: data?.['Measurement Outcome']?.contents[2]?.bullet_color ?? '#222222' }} className=" rounded-full w-[0.425rem] h-[0.425rem] mx-1"></div>
                                    </div>

                                    <div className="flex-grow grid grid-cols-12 items-center">
                                        <div className="col-span-5 bg-[#325A6D1A] rounded-[0.625rem] flex flex-col items-center px-[1.042rem] py-[0.625rem]">
                                            <Text size="md" className="font-normal">
                                                {t('Actual Uplift')}
                                            </Text>
                                            <Text size="lg" className="font-semibold">
                                                {data?.['Measurement Outcome']?.contents[2]?.actual_uplift?.toFixed(2) ?? 0} %
                                            </Text>
                                        </div>
                                        <Text size="xxxl" className="col-span-2 text-center">
                                            {' '}
                                            {getSymbol(data?.['Measurement Outcome']?.contents[2]?.actual_uplift, data?.['Measurement Outcome']?.contents[2]?.minimum_uplift)}{' '}
                                        </Text>
                                        <div className="col-span-5 bg-[#325A6D1A] rounded-[0.625rem] flex flex-col items-center px-[1.042rem] py-[0.625rem]">
                                            <Text size="md" className="font-normal" data-testid="success">
                                                {t('Minimum Uplift For Success')}
                                            </Text>
                                            <Text size="lg" className="font-semibold !text-[#28A526]">
                                                {data?.['Measurement Outcome']?.contents[2]?.minimum_uplift?.toFixed(2)} %
                                            </Text>
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    )}

                    <div className="mt-6 mb-2 bg-gray-200 rounded-2xl">
                        <div className=" flex text-center justify-center p-1">
                            <Text size="xs" className="mt-1">
                                {t('Above details are calculated based on certain criteria')}
                            </Text>
                            &nbsp;
                            <Text
                                size="xs"
                                className="underline cursor-pointer font-normal mt-1"
                                onClick={() => {
                                    trackEvent('doc_experiment_status_how_it_works_clicked', { experiment_literal })
                                    window.open(URLS.HOW_IT_WORKS_ANALYSIS_LINK, '_blank', 'noopener,noreferrer')
                                }}
                            >
                                {t('See How It Works.')}
                            </Text>
                        </div>
                    </div>
                </div>

                <div className="mt-4 flex items-center justify-end gap-3 ">
                    <Button theme="secondary" size="xs" styleType="outline" onClick={_handleCloseModal} data-testid="close-button">
                        {t('Close')}
                    </Button>
                </div>
            </div>

            <div className="modal-backdrop pointer-events-none" aria-hidden="true" onClick={(e) => e.stopPropagation()} />
        </dialog>
    )
}

export default ExperimentStatusModal
